/**
 * Global configuration
 *
 * Configure per package
 */

import { z } from 'zod'
import packageJSON from '../package.json' with { type: 'json' }
import type { TFeature } from './features.js'
import { userConfigSchema } from './manifest.schemas.js'
import type { AppManifest } from './manifest.types.js'
import { releases } from './releases.js'

const projectName = 'tunasong'

/** Manifest starts here */
const manifest = {
  name: projectName,
  version: packageJSON.version,

  releases,

  features: ['test-feature'],

  platformFlags: {
    waitlist: false,
    deleteTtlInSeconds: 60 * 60 * 24 * 30, // 30 days
  },

  auth: {
    /**
     * How long a web user stays signed in. A Cognito refresh token expires a
     * fixed time after sign-in and refreshing never extends it (rotation
     * included), so this is a hard cap, not an idle timeout. Shared by the
     * Cognito web client (`refreshTokenValidity`) and the auth-proxy refresh
     * cookie (`Max-Age`) — they must not drift apart. Browsers cap a cookie's
     * lifetime at 400 days, so going above that needs the cookie re-set on refresh.
     */
    webRefreshTokenValidityDays: 365,
  },

  resources: {
    // This is the ID of the starter content that will be replicated to new users
    starterContentId: '1ykymfpuo14t5b0ga2re4r2t9',
  },

  entityTypeMimeExtensions: {
    // Entity / plugin type to mimeType
    // We keep this here to avoid having to instantiate all plugins to extract these.
    audio: { 'audio/*': ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.mp4', '.webm', '.flac'] },
    image: { 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp'] },
    pdf: { 'application/pdf': ['.pdf'] },
    page: { 'text/markdown': ['.md'] },
    calendar: { 'text/calendar': ['.ical'] },
    evernote: { 'application/xml': ['.enex'] },
    musicxml: {
      'application/vnd.recordare.musicxml+xml': ['.musicxml'],
      'application/vnd.recordare.musicxml': ['.mxl'],
    },
    video: {
      'video/*': [
        '.mp4',
        '.webm',
        '.ogg',
        '.ogv',
        '.mov',
        '.avi',
        '.wmv',
        '.flv',
        '.m4v',
        '.mpg',
        '.mpeg',
        '.3gp',
        '.3g2',
      ],
    },
  },

  // The host (i.e., hostname:port). Either a string (exact match) or a Regexp to match the host.
  // These will be validated with custom lambdas proxies for login / logout.
  appHosts: [
    'tunasong.com',
    /^.*\.preview\.tunasong\.com$/,
    /**
     * Local dev port range. Deliberately NOT the stream-CORS allowlist: these
     * are *hostnames* for the auth proxies and are intentionally broader (any
     * `*.preview` subdomain, plus ngrok). Stream CORS allowlists *origins*
     * (scheme included) and lives in `backend/ai-api/src/lib/stream-cors.ts`.
     * The old "keep in sync with nlp-stack.ts CORS allowedOrigins" note is gone
     * because that Function-URL CORS block no longer exists — CORS moved to the
     * CloudFront edge.
     */
    /^localhost:80\d{2}$/,
    // nGrok
    'directly-honest-donkey.ngrok-free.app',
  ],

  /** This config should seldom be changed */
  tables: {
    entities: `${projectName}_entities`,
    entityUpdates: `${projectName}_entities_updates`,
    edges: `${projectName}_entities_graph`,
  },
  schemas: {},

  search: {
    /** don't index these types */
    skipIndexForTypes: [
      'trackerdata',
      'profile',
      'usersettings',
      'audiotrack',
      'midi',
      'pollresponse',
      'entitylink',
      'link',
      'ref',
      'gameresult',
    ],
    /** Keep these in search, but exclude from some views */
    excludeFromDefault: ['comment', 'audiocomment', 'profile', 'calendarevent', 'gameresult', 'agent'],
    excludeFromRecent: [
      'comment',
      'audiocomment',
      'profile',
      'calendar',
      'image',
      'ref',
      'gameresult',
      'agent',
    ],
  },
  /** entity types where we want push to clients on change */
  push: {
    entityTypes: ['comment', 'audiocomment', 'audio', 'page', 'song', 'calendarevent', 'folder', 'image'],
  },

  /**
   * Config is typically implemented by the plugin with the key
   * @todo why is this in the manifest? There was a good reason, now forgotten.
   * It makes sense to have config values here, but the schema?
   * Ideally, we define these in the plugin, and the manifest is just a list of keys.
   */
  userConfig: userConfigSchema,
} satisfies AppManifest<TFeature>

export type UserConfig = z.infer<typeof userConfigSchema>

export const defaultUserConfig = manifest.userConfig.parse({ plugins: {} })

export default manifest
